// filepath: apps/web/src/features/admin/audit/queries.ts
//
// FR-UI-ADM-14 — TanStack Query hook for GET /api/admin/audit-log.
//
// INV-1: cache key includes tenantSlug (the second segment).
// FR-UI-CORE-10: maps every HTTP error class to a typed AuditBackendError.
// AL-7: client-side RFC3339 validation rejects bad after/before inputs
//       without a server roundtrip.

import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/api";
import type {
  AuditBackendError,
  AuditFetchState,
  AuditFilterState,
  AuditListResponse,
} from "./types";

export const auditKeys = {
  all: ["admin", "audit"] as const,
  forFilter: (tenantSlug: string, filter: AuditFilterState) =>
    [...auditKeys.all, tenantSlug, JSON.stringify(filter)] as const,
};

export interface UseAuditLogResult {
  data: AuditListResponse | null;
  state: AuditFetchState;
  error: AuditBackendError | null;
}

function isRFC3339(s: string): boolean {
  if (!s) return false;
  // RFC3339 = YYYY-MM-DDTHH:MM:SS(.fff)?(Z|±HH:MM)
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(
    s,
  );
}

function buildQueryString(filter: AuditFilterState): string {
  const p = new URLSearchParams();
  if (filter.definition_type !== "all")
    p.set("definition_type", filter.definition_type);
  if (filter.author) p.set("author", filter.author);
  if (filter.after) p.set("after", filter.after);
  if (filter.before) p.set("before", filter.before);
  if (filter.cursor) p.set("cursor", filter.cursor);
  p.set("limit", "50");
  return p.toString();
}

export function useAuditLog(
  tenantSlug: string,
  filter: AuditFilterState,
): UseAuditLogResult {
  // AL-7: client-side RFC3339 validation — short-circuit to validation-error.
  if (filter.after && !isRFC3339(filter.after)) {
    return {
      data: null,
      state: "validation-error",
      error: {
        kind: "validation-error",
        message: "after: not RFC3339",
        retriable: false,
      },
    };
  }
  if (filter.before && !isRFC3339(filter.before)) {
    return {
      data: null,
      state: "validation-error",
      error: {
        kind: "validation-error",
        message: "before: not RFC3339",
        retriable: false,
      },
    };
  }

  const q = useQuery({
    queryKey: auditKeys.forFilter(tenantSlug, filter),
    queryFn: async () => {
      const qs = buildQueryString(filter);
      const res = await apiFetch(`/api/admin/audit-log?${qs}`);
      if (res.status === 401 || res.status === 403) {
        const err: AuditBackendError = {
          kind: "permission-denied",
          message: "Permission denied",
          retriable: false,
        };
        throw err;
      }
      if (res.status === 400) {
        const body = await res.json().catch(() => ({}));
        const err: AuditBackendError = {
          kind: "validation-error",
          message: (body as { error?: string }).error ?? "Invalid filter",
          retriable: false,
        };
        throw err;
      }
      if (res.status === 429) {
        const retryAfter = Number(res.headers.get("Retry-After") ?? "60");
        const err: AuditBackendError = {
          kind: "rate-limit",
          message: "Rate limited",
          retriable: true,
          retryAfterSeconds: retryAfter,
        };
        throw err;
      }
      if (!res.ok) {
        const err: AuditBackendError = {
          kind: "fetch-failure",
          message: `HTTP ${res.status}`,
          retriable: true,
        };
        throw err;
      }
      return (await res.json()) as AuditListResponse;
    },
    retry: false,
    staleTime: 15_000,
  });

  if (q.isLoading) {
    return { data: null, state: "loading", error: null };
  }
  if (q.isSuccess) {
    return { data: q.data, state: "success", error: null };
  }
  // q.isError
  const err = q.error as unknown as AuditBackendError | undefined;
  return {
    data: null,
    state: err?.kind ?? "fetch-failure",
    error: err ?? {
      kind: "fetch-failure",
      message: "Unknown error",
      retriable: true,
    },
  };
}
